import type { Agent, AgentOutputType } from './agent';
import { ModelBehaviorError, UserError } from './errors';
import type { RunItem } from './items';
import logger from './logger';
import { RunContext } from './run-context';
import type { ToolInputParameters } from './tool';
import { addErrorToCurrentSpan } from './tracing/context';
import type {
  AgentInputItem,
  JsonObjectSchema,
  ResolveParsedToolParameters,
  TextOutput,
  UnknownContext,
} from './types';
import {
  getSchemaAndParserFromInputType,
  toFunctionToolName,
} from './utils/tools';

function defaultHandoffToolName<TContext, TOutput extends AgentOutputType>(
  agent: Agent<TContext, TOutput>,
) {
  return `transfer_to_${toFunctionToolName(agent.name)}`;
}

function defaultHandoffToolDescription<
  TContext,
  TOutput extends AgentOutputType,
>(agent: Agent<TContext, TOutput>) {
  return `Handoff to the ${agent.name} agent to handle the request. ${agent.handoffDescription ?? ''}`;
}

export type HandoffEnabledFunction<TContext = UnknownContext> = (args: {
  runContext: RunContext<TContext>;
  agent: Agent<any, any>;
}) => Promise<boolean>;

export class Handoff<
  TContext = UnknownContext,
  TOutput extends AgentOutputType = TextOutput,
> {
  /**
   * 表示该交接的工具的名称。
   */
  public toolName: string;

  /**
   * 表示该交接的工具的描述。
   */
  public toolDescription: string;

  /**
   * 交接输入的 JSON Schema。如果交接不需要接收输入，可以为空。
   */
  public inputJsonSchema: JsonObjectSchema<any> = {
    type: 'object',
    properties: {},
    required: [],
    additionalProperties: false,
  };

  /**
   * 输入 JSON Schema 是否采用严格模式。我们**强烈**建议将其设置为
   * true，因为这能提高生成正确 JSON 输入的概率。
   */
  public strictJsonSchema: boolean = true;

  /**
   * 调用交接的函数。传入的参数为：
   * 1. 交接的运行上下文
   * 2. 来自 LLM 的参数，为 JSON 字符串。如果 inputJsonSchema 为空，则为空字符串。
   *
   * 必须返回一个 agent。
   */
  public onInvokeHandoff: (
    context: RunContext<TContext>,
    args: string,
  ) => Promise<Agent<TContext, TOutput>> | Agent<TContext, TOutput>;

  /**
   * 被交接到的 agent 的名称。
   */
  public agentName: string;
  /**
   * 一个过滤传入下一个 agent 的输入的函数。默认情况下，新 agent
   * 能看到完整的对话历史。在某些情况下，你可能需要过滤输入，例如，
   * 移除较早的输入，或从已有输入中移除工具。
   *
   * 该函数会接收迄今为止的完整对话历史，包括触发交接的输入项，
   * 以及一个表示交接工具输出的工具调用输出项。
   *
   * 你可以根据需要自由修改输入历史或新增项。下一个运行的 agent
   * 将接收到 `handoffInputData.allItems`。
   */
  public inputFilter?: HandoffInputFilter;

  /**
   * 被交接到的 agent。
   */
  public agent: Agent<TContext, TOutput>;

  /**
   * 返回一个可用于调用交接的函数工具定义。
   */
  getHandoffAsFunctionTool() {
    return {
      type: 'function' as const,
      name: this.toolName,
      description: this.toolDescription,
      parameters: this.inputJsonSchema,
      strict: this.strictJsonSchema,
    };
  }

  /**
   * 判断交接是否启用的函数，默认为始终启用。
   */
  public isEnabled: HandoffEnabledFunction<TContext> = async () => true;

  constructor(
    agent: Agent<TContext, TOutput>,
    onInvokeHandoff: (
      context: RunContext<TContext>,
      args: string,
    ) => Promise<Agent<TContext, TOutput>> | Agent<TContext, TOutput>,
  ) {
    this.agentName = agent.name;
    this.onInvokeHandoff = onInvokeHandoff;
    this.toolName = defaultHandoffToolName(agent);
    this.toolDescription = defaultHandoffToolDescription(agent);
    this.agent = agent;
  }
}

/**
 * Data passed to the handoff function.
 */
export type HandoffInputData = {
  /**
   * The input history before `Runner.run()` was called.
   */
  inputHistory: string | AgentInputItem[];

  /**
   * The items generated before the agent turn where the handoff was invoked.
   */
  preHandoffItems: RunItem[];

  /**
   * The new items generated during the current agent turn, including the item that triggered the
   * handoff and the tool output message representing the response from the handoff output.
   */
  newItems: RunItem[];
  /**
   * The context of the handoff.
   * Note that, since this property was added later on, it's optional to pass from users.
   */
  runContext?: RunContext<any>;
};

export type HandoffInputFilter = (input: HandoffInputData) => HandoffInputData;

/**
 * Returns a handoff for the given agent. If the agent is already wrapped into a handoff,
 * it will be returned as is. Otherwise, a new handoff instance will be created.
 *
 * @template TContext The context of the handoff
 * @template TOutput The output type of the handoff
 */
export function getHandoff<TContext, TOutput extends AgentOutputType>(
  agent: Agent<TContext, TOutput> | Handoff<TContext, TOutput>,
) {
  if (agent instanceof Handoff) {
    return agent;
  }

  return handoff(agent);
}

/**
 * A function that runs when the handoff is invoked.
 */
export type OnHandoffCallback<TInputType extends ToolInputParameters> = (
  context: RunContext<any>,
  input?: ResolveParsedToolParameters<TInputType>,
) => Promise<void> | void;

/**
 * A handoff is when an agent delegates a task to another agent.
 * For example, in a customer support scenario you might have a "triage agent" that determines which
 * agent should handle the user's request, and sub-agents that specialize in different areas like
 * billing, account management, etc.
 *
 * @template TContext The context of the handoff
 * @template TOutput The output type of the handoff
 */
type HandoffEnabledPredicate<TContext = UnknownContext> = (args: {
  runContext: RunContext<TContext>;
  agent: Agent<any, any>;
}) => boolean | Promise<boolean>;

type HandoffEnabledOption<TContext> =
  | boolean
  | HandoffEnabledPredicate<TContext>;

/**
 * Configuration for a handoff.
 */
export type HandoffConfig<
  TInputType extends ToolInputParameters,
  TContext = UnknownContext,
> = {
  /**
   * Optional override for the name of the tool that represents the handoff.
   */
  toolNameOverride?: string;

  /**
   * Optional override for the description of the tool that represents the handoff.
   */
  toolDescriptionOverride?: string;

  /**
   * A function that runs when the handoff is invoked
   */
  onHandoff?: OnHandoffCallback<TInputType>;

  /**
   * The type of the input to the handoff. If provided as a Zod schema, the input will be validated
   * against this type. Only relevant if you pass a function that takes an input
   */
  inputType?: TInputType;

  /**
   * A function that filters the inputs that are passed to the next agent.
   */
  inputFilter?: HandoffInputFilter;

  /**
   * Determines whether the handoff should be available to the model for the current run.
   */
  isEnabled?: HandoffEnabledOption<TContext>;
};

/**
 * Creates a handoff from an agent. Handoffs are automatically created when you pass an agent
 * into the `handoffs` option of the `Agent` constructor. Alternatively, you can use this function
 * to create a handoff manually, giving you more control over configuration.
 *
 * @template TContext The context of the handoff
 * @template TOutput The output type of the handoff
 * @template TInputType The input type of the handoff
 */
export function handoff<
  TContext = UnknownContext,
  TOutput extends AgentOutputType = TextOutput,
  TInputType extends ToolInputParameters = ToolInputParameters,
>(
  agent: Agent<TContext, TOutput>,
  config: HandoffConfig<TInputType, TContext> = {},
) {
  let parser: ((input: string) => Promise<any>) | undefined;

  const hasOnHandoff = !!config.onHandoff;
  const hasInputType = !!config.inputType;
  const hasBothOrNeitherHandoffAndInputType = hasOnHandoff === hasInputType;

  if (!hasBothOrNeitherHandoffAndInputType) {
    throw new UserError(
      'You must provide either both `onHandoff` and `inputType` or neither.',
    );
  }

  async function onInvokeHandoff(
    context: RunContext<any>,
    inputJsonString?: string,
  ) {
    if (parser) {
      if (!inputJsonString) {
        addErrorToCurrentSpan({
          message: `Handoff function expected non empty input but got: ${inputJsonString}`,
          data: {
            details: `input is empty`,
          },
        });
        throw new ModelBehaviorError(
          'Handoff function expected non empty input',
        );
      }
      try {
        // verify that it's valid input but we don't care about the result
        const parsed = await parser(inputJsonString);
        if (config.onHandoff) {
          await config.onHandoff(context, parsed);
        }
      } catch (error) {
        addErrorToCurrentSpan({
          message: `Invalid JSON provided`,
          data: {},
        });
        if (!logger.dontLogToolData) {
          logger.error(
            `Invalid JSON when parsing: ${inputJsonString}. Error: ${error}`,
          );
        }
        throw new ModelBehaviorError('Invalid JSON provided');
      }
    } else {
      await config.onHandoff?.(context);
    }

    return agent;
  }

  const handoff = new Handoff(agent, onInvokeHandoff);

  if (typeof config.isEnabled === 'function') {
    const predicate = config.isEnabled as HandoffEnabledPredicate<TContext>;
    handoff.isEnabled = async ({ runContext, agent }) => {
      const result = await predicate({ runContext, agent });
      return Boolean(result);
    };
  } else if (typeof config.isEnabled === 'boolean') {
    handoff.isEnabled = async () => config.isEnabled as boolean;
  }

  if (config.inputType) {
    const result = getSchemaAndParserFromInputType(
      config.inputType,
      handoff.toolName,
      { strict: true },
    );
    handoff.inputJsonSchema = result.schema;
    handoff.strictJsonSchema = true;
    parser = result.parser;
  }

  if (config.toolNameOverride) {
    handoff.toolName = config.toolNameOverride;
  }

  if (config.toolDescriptionOverride) {
    handoff.toolDescription = config.toolDescriptionOverride;
  }

  if (config.inputFilter) {
    handoff.inputFilter = config.inputFilter;
  }

  return handoff;
}

/**
 * Generates the message that will be given as tool output to the model that requested the handoff.
 *
 * @param agent The agent to transfer to
 * @returns The message that will be given as tool output to the model that requested the handoff
 */
export function getTransferMessage<TContext, TOutput extends AgentOutputType>(
  agent: Agent<TContext, TOutput>,
) {
  return JSON.stringify({ assistant: agent.name });
}
